import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WheelPickerWrapper, WheelPicker } from '@ncdai/react-wheel-picker';
import { parseInt, range } from 'lodash';
import styled from 'styled-components';
import { FlexColumn } from 'src/components/layout/flex-column';
import { FlexRow } from 'src/components/layout/flex-row';
import { useDateHelpers } from 'src/hooks/use-date-helpers';
import { useVisible } from 'src/hooks/use-visible';
import { ButtonV2 } from '../../button/lgg-button';
import { LggModal } from '../../modals/lgg-modal';
import '@ncdai/react-wheel-picker/style.css';
import { TextInput } from '../text-input';

const hourOptions = range(1, 13).map((index) => ({
  value: index,
  label: index,
}));

const minutesOptions = range(0, 60).map((index) => ({
  value: index,
  label: index,
}));

enum Meridiem {
  AM = 'AM',
  PM = 'PM',
}

const meridiemOptions = [
  {
    label: 'AM',
    value: Meridiem.AM,
  },
  {
    label: 'PM',
    value: Meridiem.PM,
  },
];

const StyleContainer = styled(FlexColumn)`
  height: 184px;
  justify-content: center;
  padding: 10px 0;

  [data-rwp='true'] {
    top: -30px;
  }

  [data-rwp-wrapper] {
    gap: 20px;
    padding: 0 20px;
  }

  [data-rwp-wrapper] li {
    height: 60px;
  }

  [data-rwp-highlight-item] {
    color: ${({ theme }) => theme.colors.gogo};
    font-family: ${({ theme }) => theme.font.medium};
    font-size: 18px;
    line-height: 23px;
    text-align: center;
  }

  [data-rwp-option] {
    color: ${({ theme }) => theme.colors.flint};
    font-family: ${({ theme }) => theme.font.regular};
    font-size: 17px;
    line-height: 23px;
    opacity: 0.5;
  }

  [data-rwp-highlight-wrapper] {
    background: ${({ theme }) => theme.colors.porcelain};
    border-radius: 6px;
    border: 1px solid ${({ theme }) => theme.colors.koala};
  }

  [data-rwp-highlight-wrapper][data-rwp-focused] {
    box-shadow: inset 0 0 0 2px #d4d4d8;
  }
`;

const HeaderTitle = styled.span`
  color: ${({ theme }) => theme.colors.carbon};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 20px;
  line-height: 23px;
  letter-spacing: -0.36px;
`;

const StyledModal = styled(LggModal)`
  .ant-modal-content {
    margin: 0 auto;
    width: 306px;

    .ant-modal-close {
      right: 23px;
      top: 19px;
    }
  }

  .ant-modal-header {
    padding: 17px 20px;
  }
`;

const StyledButton = styled(ButtonV2)`
  flex: 1;
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 14px;
  line-height: 18px;
`;

const OPTION_ITEM_HEIGHT = 60;

type MobileWheelTimePickerProps = {
  onApply: (date: Date | null) => void;
  value?: Date;
};

export const MobileWheelTimePicker = ({ value, onApply }: MobileWheelTimePickerProps) => {
  const [hour, setHour] = useState<number>(1);
  const [minute, setMinute] = useState<number>(1);
  const [meridiem, setMeridiem] = useState<Meridiem>(Meridiem.AM);
  const { initDayJsDate, format } = useDateHelpers();
  const visibilityHandler = useVisible();
  const { t } = useTranslation(['common']);
  const innerValue = useMemo(() => value ?? new Date(), [value]);

  useEffect(() => {
    const baseDateParsed = initDayJsDate(innerValue);
    const hour = parseInt(baseDateParsed.format('h'));
    const minute = parseInt(baseDateParsed.format('mm'));
    const meridiem = baseDateParsed.format('A');
    setHour(hour);
    setMinute(minute);
    setMeridiem(meridiem as Meridiem);
  }, [initDayJsDate, innerValue]);

  const handleApply = () => {
    const hourToSet = meridiem === Meridiem.PM ? (hour % 12) + 12 : hour % 12;
    const newDate = initDayJsDate(innerValue).set({
      hours: hourToSet,
      minutes: minute,
    });

    onApply(newDate.toDate());
    visibilityHandler.close();
  };

  return (
    <>
      <TextInput
        value={value ? format(value, 'hh:mm A') : undefined}
        placeholder={t('common:inputPlaceholder.time')}
        onClick={visibilityHandler.show}
        readOnly
        onClear={() => {
          onApply(null);
        }}
        reserveErrorArea={false}
      />
      <StyledModal
        onClose={visibilityHandler.close}
        visible={visibilityHandler.visible}
        footer={
          <FlexRow>
            <StyledButton size="small" variant="primary" onClick={handleApply}>
              {t('common:filters.actionsApply')}
            </StyledButton>
          </FlexRow>
        }
        title={<HeaderTitle>{t('common:inputPlaceholder.time')}</HeaderTitle>}
      >
        <StyleContainer>
          <WheelPickerWrapper>
            <WheelPicker
              options={hourOptions}
              onValueChange={(value) => {
                setHour(Number(value));
              }}
              value={hour}
              optionItemHeight={OPTION_ITEM_HEIGHT}
              visibleCount={12}
            />
            <WheelPicker
              options={minutesOptions}
              optionItemHeight={OPTION_ITEM_HEIGHT}
              visibleCount={12}
              onValueChange={(value) => {
                setMinute(Number(value));
              }}
              value={minute}
            />
            <WheelPicker<Meridiem>
              options={meridiemOptions}
              optionItemHeight={OPTION_ITEM_HEIGHT}
              visibleCount={12}
              onValueChange={(value) => {
                setMeridiem(value);
              }}
              value={meridiem}
            />
          </WheelPickerWrapper>
        </StyleContainer>
      </StyledModal>
    </>
  );
};
